<template>
    <div class="mt-5">
	    <!-- Question -->
	    <div v-if="data.meta" class="d-flex flex-column gap-3 text-center mb-5">
		    <h3 v-if="questionDescription" class="fw-semi-bold m-0" v-html="questionDescription"></h3>
		    <p v-if="questionSubText" class="text-secondary m-0" v-html="questionSubText"></p>
	    </div>

        <!-- Input -->
        <div class="d-flex flex-column gap-4">
            <div class="align-self-center gx-2">

                <button class="btn border-0 rounded-pill px-3 unit-btn" v-for="(option, index) in data.units" :class="{
                    'btn-dark': selectedUnit === option,
                    'btn-white bg-grey-medium unit-not-selected': selectedUnit !== option,
                    'me-2 mb-1': index < data.units.length - 1
                }" @click="selectUnit(option)">
                    {{ option }}
                </button>

            </div>

            <div class="d-flex justify-content-between align-items-center slider-container user-select-none border-active-darken"
                :class="{ 'border-danger': parseFloat(value) < parseFloat(valMin) || parseFloat(value) > parseFloat(valMax) }">

                <button class="btn btn-dark rounded-circle slider-btn" @mousedown="handlePress($event, 'decrement')"
                    @mouseup="handleRelease($event, 'decrement')" @touchstart.prevent="handlePress($event, 'decrement')"
                    @touchend="handleRelease($event, 'decrement')"
                    @touchcancel="handleRelease($event, 'decrement')">-</button>

                <div class="d-flex align-items-center gap-2 flex-grow-1 justify-content-center" @click="focusInput">
                    <input ref="valueInput" type="text" class="form-control m-0 fs-lg fw-bold text-end dyna-input p-0"
                        v-model="value" :style="{ width: inputWidth }" @blur="blurInput" @keyup.enter="blurInput">
                    <span class="text-secondary fs-md">{{ unit }}</span>
                </div>

                <button class="btn btn-dark rounded-circle slider-btn" @mousedown="handlePress($event, 'increment')"
                    @mouseup="handleRelease($event, 'increment')" @touchstart.prevent="handlePress($event, 'increment')"
                    @touchend="handleRelease($event, 'increment')"
                    @touchcancel="handleRelease($event, 'increment')">+</button>
            </div>

            <!-- Error message -->
            <p v-if="parseFloat(value) < parseFloat(valMin)" class="text-danger my-3">Please enter a value greater than {{ valMin }}</p>

        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
	questionDescription: String,
	questionSubText: String,
    data: Object,
	answer: {
		type: Array,
		default: () => []
	}
});
const valMin = ref(props.data.valMin || 0);
const valMax = ref(props.data.valMax);

const selectedUnit = ref(props.answer && props.answer.length ? props.answer[0].text : props.data.units[0]);
const value = ref(props.answer && props.answer.length ? props.answer[0].value : valMin.value);
const valueInput = ref(HTMLInputElement);
const blurInput = () => {
    valueInput.value.blur();
	validateValue(value.value);
}

const emit = defineEmits(['question-answered']);
watch(value, (newVal, oldVal) => {
	// validateValue(newVal, oldVal);
})

const validateValue = (newVal, oldVal = null) => {
	if (newVal.trim() === '') {
		emit('question-answered', null);
		return;
	}
	let correctedVal = parseInt(newVal);
	if (isNaN(correctedVal) && oldVal !== null) {
		value.value = oldVal;
		return;
	}

	correctedVal = Math.max(correctedVal, valMin.value);
	correctedVal = Math.min(correctedVal, valMax.value);
	value.value = correctedVal.toString();

	let answerToReturn = [{ value: correctedVal, text: selectedUnit.value }];

	emit('question-answered', answerToReturn);
}


const unit = computed(() => selectedUnit.value);
const inputWidth = computed(() => `${Math.max(value.value, 1).toString().length}ch`);

const selectUnit = (unit) => {
    selectedUnit.value = unit;
};

const focusInput = () => {
    valueInput.value.focus();
    valueInput.value.select();
}

let pressedTimer = null;
const pressThreshold = 200;

const updateCount = (action) => {
    let currentValue = parseInt(value.value) || 0;
    if (action === 'increment') {
        value.value = (currentValue + 1).toString();
    } else if (action === 'decrement') {
        value.value = (currentValue - 1).toString();
    }
	validateValue(value.value, currentValue.toString());
}

const handlePress = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    e.target.style.backgroundColor = '#cccccc';
    e.target.style.transform = "scale(0.95)";

    if (pressedTimer) {
        clearInterval(pressedTimer);
    }

    updateCount(action);
    pressedTimer = setTimeout(() => {
        pressedTimer = setInterval(() => updateCount(action), pressThreshold);
    }, pressThreshold);
}

const handleRelease = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    e.target.style.removeProperty('background-color');
    e.target.style.removeProperty('transform');

    clearInterval(pressedTimer);
    pressedTimer = null;
}
</script>

<style scoped lang="scss">
.fs-md {
    font-size: 1.6em !important;
}

.fs-lg {
    font-size: 1.75em !important;
}

.slider-container {
    border-radius: 999px;
    border: 1px solid #6c757d;
    padding: 0.25em 0.6em;
}

.dyna-input {
    border: none !important;
    width: 1ch;
    box-sizing: content-box;
}

@media (max-width: 992px) {
    .unit-btn {

        &:hover,
        &:active,
        &:focus {
            background: #212529 !important;
        }

        &.unit-not-selected {

            &:hover,
            &:active,
            &:focus {
                background: #ecf0f5 !important;
            }
        }
    }
}

.unit-btn {
    &.unit-not-selected {
        &:hover {
            background-color: #d4d8dc;
        }
    }
}

.slider-btn {
    width: 36px;
    height: 36px;
    transition: all .15s ease-in-out;
    background-color: transparent;
    border: none;
    color: #212529;

    transform-origin: center;
    will-change: transform;

    &:hover {
        background-color: #e9e9e9;
        color: #000;
    }
}
</style>